import { create } from 'zustand';

import { makeChainAddressId } from '@infinex/asset-config';
import type {
  ChainBalancesDataset,
  CollectiblesDataset,
  InxDataset,
  PerpsDataset,
} from '@infinex/types';

import type { ComputedUnifiedBalances } from './utils/computeUnifiedBalances';
import { computeUnifiedBalances } from './utils/computeUnifiedBalances';
import type { ComputedUsdBalanceTotals } from './utils/computeUsdTotals';
import { computeUsdTotals } from './utils/computeUsdTotals';
import type { PrecomputedBalances } from './utils/precomputeBalances';
import { precomputeBalances } from './utils/precomputeBalances';

import type { AccountAddress } from '../../../types';

interface UnifiedBalancesState {
  precomputed: PrecomputedBalances | null;
  balancesCache: Map<string, ComputedUnifiedBalances>;
  usdTotalsCache: Map<string, ComputedUsdBalanceTotals>;
}

interface UnifiedBalancesActions {
  /**
   * Precompute values for unified balances calculation.
   * This is the expensive computation that happens once per dataset update.
   *
   * Note: This should be called from BalancesProvider, not from individual hook instances.
   */
  precompute: (
    chainBalancesDataset: ChainBalancesDataset,
    inxDataset: InxDataset,
    perpsDataset: PerpsDataset,
    inxPrice: number | null,
    collectiblesDataset?: CollectiblesDataset
  ) => void;

  /**
   * Select balances for specific wallet addresses.
   */
  selectBalances: (
    accountAddresses: AccountAddress[]
  ) => ComputedUnifiedBalances | null;

  /**
   * Select USD balance totals for specific wallet addresses.
   */
  selectUsdTotals: (
    accountAddresses: AccountAddress[],
    totalPerpsPnl24h: number,
    includeLockedPatronNfts: boolean
  ) => ComputedUsdBalanceTotals | null;

  clear: () => void;
}

export type UnifiedBalancesStore = UnifiedBalancesState &
  UnifiedBalancesActions;

export const useUnifiedBalancesStore = create<UnifiedBalancesStore>(
  (set, get) => ({
    ...getInitialState(),

    precompute: (
      chainBalancesDataset: ChainBalancesDataset,
      inxDataset: InxDataset,
      perpsDataset: PerpsDataset,
      inxPrice: number | null,
      collectiblesDataset?: CollectiblesDataset
    ) => {
      const precomputed = precomputeBalances(
        chainBalancesDataset,
        inxDataset,
        perpsDataset,
        inxPrice,
        collectiblesDataset
      );

      set({ precomputed, balancesCache: new Map(), usdTotalsCache: new Map() });
    },

    selectBalances: (
      accountAddresses: AccountAddress[]
    ): ComputedUnifiedBalances | null => {
      const { precomputed, balancesCache } = get();

      if (!precomputed) {
        return null;
      }

      const cacheKey = getAccountAddressCacheKey(accountAddresses);

      const cached = balancesCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const allBalances = accountAddresses.flatMap((addr) => {
        const walletKey = makeChainAddressId(addr.chain, addr.address);
        return precomputed.balancesByWalletAddress[walletKey] ?? [];
      });

      const result = computeUnifiedBalances(
        allBalances,
        accountAddresses,
        precomputed
      );

      balancesCache.set(cacheKey, result);

      return result;
    },

    selectUsdTotals: (
      accountAddresses: AccountAddress[],
      totalPerpsPnl24h: number,
      includeLockedPatronNfts: boolean
    ): ComputedUsdBalanceTotals | null => {
      const { precomputed, selectBalances, usdTotalsCache } = get();

      if (!precomputed) {
        return null;
      }

      const addressKey = getAccountAddressCacheKey(accountAddresses);
      const cacheKey = `${addressKey}|totalPerpsPnl24h:${totalPerpsPnl24h}|includePatrons:${includeLockedPatronNfts}`;

      const cached = usdTotalsCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const unifiedBalances = selectBalances(accountAddresses);

      if (!unifiedBalances) {
        return null;
      }

      const result = computeUsdTotals(
        unifiedBalances,
        totalPerpsPnl24h,
        includeLockedPatronNfts
      );

      usdTotalsCache.set(cacheKey, result);

      return result;
    },

    clear: () => {
      set(getInitialState());
    },
  })
);

function getInitialState() {
  const initialState: UnifiedBalancesState = {
    precomputed: null,
    balancesCache: new Map(),
    usdTotalsCache: new Map(),
  };
  return initialState;
}

function getAccountAddressCacheKey(accountAddresses: AccountAddress[]) {
  return accountAddresses
    .map((addr) => makeChainAddressId(addr.chain, addr.address))
    .join('|');
}
